/**
 * The addresses of «Data» (product owner, 2026-10-14): a resource per path, so a person or a video
 * has its own link to share.
 *
 * - `/virtual-profiler/data/videos`: the gallery; a group and «sin persona» stay filters (`?group=`,
 *   `?person=none`).
 * - `/virtual-profiler/data/persons`: the grid of people.
 * - `/virtual-profiler/data/persons/:personId`: one person's page.
 * - `/virtual-profiler/data/videos/:videoId`: one video's review.
 *
 * The old `/virtual-profiler/videos?view=persons|person=N` and `/virtual-profiler/videos/:videoId`
 * redirect here, so links already shared keep working.
 */
import type { LocationQuery, LocationQueryRaw, RouteLocationRaw } from 'vue-router';
import { datasetPersonFrom } from './persons.js';

export const DATA_VIDEOS_ROUTE = 'virtual-profiler-videos';
export const DATA_PERSONS_ROUTE = 'virtual-profiler-persons';
export const DATA_PERSON_ROUTE = 'virtual-profiler-person';

export function dataVideosLocation(query: LocationQueryRaw = {}): RouteLocationRaw {
  return { name: DATA_VIDEOS_ROUTE, query };
}

export function dataPersonsLocation(): RouteLocationRaw {
  return { name: DATA_PERSONS_ROUTE };
}

/** A person's page, or the gallery of nobody's videos for `none`. */
export function dataPersonLocation(personId: number | 'none'): RouteLocationRaw {
  return personId === 'none'
    ? dataVideosLocation({ person: 'none' })
    : { name: DATA_PERSON_ROUTE, params: { personId: String(personId) } };
}

/** Where an old `/virtual-profiler/videos?…` address lives now. */
export function legacyDataRedirect(to: { query: LocationQuery }): RouteLocationRaw {
  const { view, person, ...rest } = to.query;
  const selection = datasetPersonFrom(person);

  // The query is always given: a redirect without one keeps the old `?person=` and `?view=`.
  if (typeof selection === 'number') {
    return { name: DATA_PERSON_ROUTE, params: { personId: String(selection) }, query: rest };
  }
  if (view === 'persons') return { name: DATA_PERSONS_ROUTE, query: rest };

  return { name: DATA_VIDEOS_ROUTE, query: selection === 'none' ? { ...rest, person } : rest };
}
